import { ChangeDetectionStrategy, Component, computed, effect, inject, model, output, signal, untracked } from '@angular/core';
import moment from 'moment';
import { PlatformIChingConsultationDto } from '@data/_generated-api/platform-api/models/platform-i-ching-consultation-dto';
import { ESplashLoaderComponent } from '@shared/components/loaders/e-splash-loader/e-splash-loader.component';
import { SharedModule } from '@shared/shared.module';
import { momentLocaleFor } from '@shared/utils/moment-locale.utils';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../chart/bodygraph/hd-content-language.service';
import { HdIChingConsultComponent } from '../consult/hd-iching-consult.component';
import { injectIChingTranslate } from '../consult/iching-consult-translate';
import { HdIChingAccessService } from '../hd-iching-access.service';
import { HdIChingJournalService } from '../journal/hd-iching-journal.service';
import { HdIChingMomentDialogTarget, IChingConsultContext } from './hd-iching-moment.models';
import { HD_ICHING_IN_MODAL } from './hd-iching-modal-panel';
import { HdIChingMomentsService } from './hd-iching-moments.service';

/**
 * I Tjing-momentdialoog: een raadpleging uit het dagboek lezen (vanaf de
 * tijdlijn of een kalender) of een nieuwe starten voor een chart op een
 * tijdstip (momentdialoog, dag-flow). Toont de bestaande raadpleegcomponent
 * ingebed (vraag, worp, duiding met contextkaart en transitblok); bewaren
 * meldt `HdIChingMomentsService.changed`, zodat tijdlijn en kalenders
 * herladen. Zonder `classicVisible()` opent er niets.
 *
 * `e-dialog` sluit meteen en meldt pas daarna `openChange(false)`; een
 * sluit-bevestiging bij onbewaarde invoer is daar niet in te bouwen. De
 * Bewaren-knop in de duiding en de bewaard-indicator maken de stand zichtbaar.
 *
 * `HD_ICHING_IN_MODAL` laat het klassieke leespaneel hier altijd zwevend
 * openen: gedockt zou het achter de dialoog verdwijnen.
 */
@Component({
  selector: 'hd-iching-moment-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, ESplashLoaderComponent, HdIChingConsultComponent],
  // Leespaneel-opens uit de ingebedde raadpleging (transitblok, duiding) zwevend boven de dialoog.
  providers: [{ provide: HD_ICHING_IN_MODAL, useValue: true }],
  styles: `
    .state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; min-height: 160px; }
    .retry {
      padding: 4px 12px; border: 1px solid var(--hd-iching-muted-border, rgba(0, 0, 0, 0.18)); border-radius: var(--radius-md, 6px);
      background: transparent; color: inherit; font: inherit; font-size: 13px; cursor: pointer;
    }
  `,
  template: `
    <e-dialog
      [open]="open()"
      (openChange)="onOpenChange($event)"
      [title]="title()"
      [titleData]="titleData()"
      size="xl"
      panelClass="iching-moment-dialog-pane"
    >
      <ng-template>
        @if (error()) {
          <div class="state" role="alert" data-test-id="iching-moment-error">
            <p>{{ t('humanDesign.iching.moment.loadError') }}</p>
            <button type="button" class="retry" data-test-id="iching-moment-retry" (click)="reload()">{{ t('humanDesign.iching.panel.retry') }}</button>
          </div>
        } @else if (context(); as ctx) {
          <hd-iching-consult embedded [context]="ctx" (saved)="onSaved($event)" />
        } @else {
          <div class="state" role="status" data-test-id="iching-moment-loading"><e-splash-loader [size]="40" /></div>
        }
      </ng-template>
    </e-dialog>
  `,
})
export class HdIChingMomentDialogComponent {
  /** null = gesloten. */
  readonly target = model<HdIChingMomentDialogTarget | null>(null);
  /** Na elke geslaagde bewaarbeurt in de dialoog. */
  readonly consultationSaved = output<PlatformIChingConsultationDto>();

  private readonly journal = inject(HdIChingJournalService);
  private readonly access = inject(HdIChingAccessService);
  private readonly moments = inject(HdIChingMomentsService);
  private readonly contentLang = inject(HdContentLanguageService);
  protected readonly t = injectIChingTranslate();

  protected readonly context = signal<IChingConsultContext | undefined>(undefined);
  protected readonly error = signal(false);
  /** Laatst bewaarde raadpleging: titel volgt het tijdstip en de hexagrammen. */
  private readonly lastSaved = signal<PlatformIChingConsultationDto | undefined>(undefined);
  private loadGeneration = 0;

  protected readonly open = computed(() => this.target() != null && this.access.classicVisible());

  private readonly lang = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly title = computed(() =>
    this.target()?.kind === 'new' && this.lastSaved() === undefined
      ? this.t('humanDesign.iching.moment.dialogTitleNew')
      : this.t('humanDesign.iching.moment.dialogTitle'),
  );

  /** "{chart} · {datum tijd}" of "#53 ↔ #57 · {datum tijd}" voor een bestaande raadpleging. */
  protected readonly titleData = computed(() => {
    const target = this.target();
    if (target == null) {
      return '';
    }
    const parts: string[] = [];
    const saved = this.lastSaved() ?? this.context()?.consultation;
    if (target.kind === 'new') {
      parts.push(target.chartName);
    } else if (target.chartName) {
      parts.push(target.chartName);
    }
    if (saved?.primaryHexagram != null) {
      parts.push(saved.changedHexagram != null ? `#${saved.primaryHexagram} ↔ #${saved.changedHexagram}` : `#${saved.primaryHexagram}`);
    }
    const at = saved?.consultedAt ?? (target.kind === 'new' ? target.atUtc : undefined);
    if (at) {
      parts.push(moment.utc(at).local().locale(momentLocaleFor(this.lang())).format('D MMM YYYY, HH:mm'));
    }
    return parts.filter(part => part.length > 0).join(' · ');
  });

  constructor() {
    effect(() => {
      const target = this.target();
      untracked(() => this.applyTarget(target));
    });
  }

  protected reload(): void {
    this.applyTarget(this.target());
  }

  protected onOpenChange(open: boolean): void {
    if (!open) {
      this.target.set(null);
    }
  }

  protected onSaved(dto: PlatformIChingConsultationDto): void {
    this.lastSaved.set(dto);
    this.moments.markChanged();
    this.consultationSaved.emit(dto);
  }

  private applyTarget(target: HdIChingMomentDialogTarget | null): void {
    this.loadGeneration++;
    this.error.set(false);
    this.lastSaved.set(undefined);
    if (target == null || !this.access.classicVisible()) {
      this.context.set(undefined);
      return;
    }
    if (target.kind === 'new') {
      this.context.set({ chartId: target.chartId, chartName: target.chartName, atUtc: target.atUtc });
      return;
    }
    this.context.set(undefined);
    void this.loadConsultation(target.consultationId, target.chartName);
  }

  private async loadConsultation(id: string, chartName: string | undefined): Promise<void> {
    const generation = this.loadGeneration;
    try {
      const consultation = await this.journal.get(id);
      if (generation === this.loadGeneration) {
        this.context.set({ consultation, chartName, chartId: consultation.linkedChartId ?? undefined });
      }
    } catch {
      if (generation === this.loadGeneration) {
        this.error.set(true);
      }
    }
  }
}
